import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Upload, FileText, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle,
  Users, Calendar as CalendarIcon, Plus, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';
import { getCategoryColor } from '../utils/categoryColors';

/**
 * Bulk load timers from an APTP schedule + groups XLSX.
 *
 * Flow:
 *   1. User picks a Cronograma PDF (required) and optionally the GRUPOS XLSX.
 *   2. Backend parses both and returns { sessions[], pilots[] }.
 *   3. User picks a pilot from the dropdown. We auto-filter sessions by:
 *        - the pilot's class (detected from the pilot presence in the XLSX)
 *        - the pilot's entrenamiento_group for ENTRENAMIENTO sessions
 *        - the pilot's clasificacion_group for CLASIFICACIÓN sessions
 *   4. User can override group / add/remove sessions with checkboxes.
 *   5. Confirm → POST /api/timers/bulk-create → new timers appear in the list.
 */
const BulkTimerImport = ({ open, onOpenChange, onCreated }) => {
  const { t } = useTranslation();
  const cronoRef = useRef(null);
  const gruposRef = useRef(null);

  // Rotax weekends ship as TWO PDFs (Viernes practice + Sábado/Domingo
  // official event). We accept up to 2 files here and forward both to
  // `/api/timers/bulk-parse`, which merges + dedups server-side. Any
  // non-Rotax cronograma still works exactly the same (upload 1 PDF).
  const [cronogramaFiles, setCronogramaFiles] = useState([]);
  const [gruposFile, setGruposFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [creating, setCreating] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState('');
  // Multi-pilot rows. Each row: { id, mode: 'select'|'manual', number, name,
  // entrenGroup, clasifGroup }. Starts with one empty row.
  const _blankRow = () => ({
    id: `row-${Math.random().toString(36).slice(2, 9)}`,
    mode: 'select', // 'select' from XLSX list, or 'manual'
    number: '',
    name: '',
    entrenGroup: '',
    clasifGroup: '',
  });
  const [rows, setRows] = useState([_blankRow()]);
  const [disabledIds, setDisabledIds] = useState(new Set()); // key format: `${rowId}::${sessionKey}`

  const reset = () => {
    setCronogramaFiles([]);
    setGruposFile(null);
    setParsed(null);
    setSelectedCategory('');
    setRows([_blankRow()]);
    setDisabledIds(new Set());
    if (cronoRef.current) cronoRef.current.value = '';
    if (gruposRef.current) gruposRef.current.value = '';
  };

  const handleParse = async () => {
    if (!cronogramaFiles.length) {
      toast.error(t('bulk_timer.error_no_crono'));
      return;
    }
    setParsing(true);
    try {
      const fd = new FormData();
      // Multi-file: FastAPI reads all "cronograma" parts into a list.
      for (const f of cronogramaFiles) fd.append('cronograma', f);
      if (gruposFile) fd.append('grupos', gruposFile);
      const res = await axios.post(`${API}/timers/bulk-parse`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setParsed(res.data);
      // Auto-pick most-common category so the UI is useful even without XLSX.
      if (res.data.categories_detected?.length) {
        setSelectedCategory(res.data.categories_detected[0]);
      }
      // Force first row to manual mode if there's no XLSX to pick pilots from.
      if (!res.data.pilots || res.data.pilots.length === 0) {
        setRows([{ ..._blankRow(), mode: 'manual' }]);
      }
      if (res.data.warnings?.length) {
        toast.info(t('bulk_timer.warn_toast', { count: res.data.warnings.length }));
      } else {
        toast.success(
          t('bulk_timer.summary', {
            sessions: res.data.sessions.length,
            pilots: res.data.pilots.length,
            categories: (res.data.categories_detected || []).join(', '),
          }),
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('bulk_timer.error_parse'));
    } finally {
      setParsing(false);
    }
  };

  const pilotsIndex = useMemo(() => {
    const map = new Map();
    for (const p of parsed?.pilots || []) map.set(p.number, p);
    return map;
  }, [parsed]);

  // Resolve each row into an "effective" pilot spec + suggested sessions.
  // A row is valid when it has a name (either picked from XLSX or typed).
  const rowSpecs = useMemo(() => {
    if (!parsed) return [];
    // When the parsed cronograma has no pilot list (no XLSX), every row is
    // implicitly manual — regardless of what `r.mode` says. Otherwise a
    // freshly-added row (default mode: 'select') would silently render as
    // manual inputs (correctly, because there's nothing to pick from) but
    // never resolve to a pilot object → its timers were being skipped.
    const forceManual = pilotsIndex.size === 0;
    return rows.map((r) => {
      const effectiveMode = forceManual ? 'manual' : r.mode;
      let pilot = null;
      if (effectiveMode === 'select' && r.number) {
        const p = pilotsIndex.get(r.number);
        if (p) pilot = { name: p.name, number: p.number, source: 'xlsx' };
      } else if (effectiveMode === 'manual' && r.name.trim()) {
        pilot = { name: r.name.trim(), number: r.number.trim() || null, source: 'manual' };
      }
      // Effective groups: row override OR XLSX default (if pilot came from XLSX).
      const xlsxPilot = effectiveMode === 'select' ? pilotsIndex.get(r.number) : null;
      const entrenGroup = r.entrenGroup || xlsxPilot?.entrenamiento_group || '';
      const clasifGroup = r.clasifGroup || xlsxPilot?.clasificacion_group || '';
      const sessions = pilot
        ? parsed.sessions.filter((s) => {
            if (selectedCategory && s.category !== selectedCategory) return false;
            // APTP/Carshow/TC flow: filter by pilot's A/B/C group.
            if (s.session_type === 'entrenamiento') {
              if (!s.group) return true;
              return entrenGroup && s.group === entrenGroup;
            }
            if (s.session_type === 'clasificacion') {
              // APTP legacy: only up to CLASIFICACIÓN II per user's spec.
              // Rotax cronogramas emit session_number = 0 (single-shot),
              // so the ">2" guard is a no-op for karting.
              if (s.session_number > 2) return false;
              if (!s.group) return true;
              return clasifGroup && s.group === clasifGroup;
            }
            // Rotax karting session types: no groups → include everything
            // in the selected category. The user asked (Feb 2026): "en
            // Rotax no hay grupos de entrenamientos ni de clasificación,
            // es un solo grupo para todos... lo mismo para las mangas y
            // final". If the PDF DID attach a (Grupo A/B) suffix (only
            // happens on Sunday's Senior Nacional split), the pilot's
            // clasifGroup selector still filters it.
            if (['tanda', 'manga', 'serie', 'final', 'warmup', 'repechaje', 'prefinal'].includes(s.session_type)) {
              if (!s.group) return true;
              return clasifGroup && s.group === clasifGroup;
            }
            return false;
          })
        : [];
      return { row: r, pilot, entrenGroup, clasifGroup, sessions, xlsxPilot };
    });
  }, [parsed, rows, selectedCategory, pilotsIndex]);

  const totalActiveSessions = useMemo(
    () => rowSpecs.reduce(
      (acc, sp) => acc + sp.sessions.filter(
        (s) => !disabledIds.has(`${sp.row.id}::${_sessionKey(s)}`),
      ).length,
      0,
    ),
    [rowSpecs, disabledIds],
  );

  const validPilotCount = rowSpecs.filter((sp) => sp.pilot).length;

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const addRow = () => setRows((prev) => [...prev, _blankRow()]);
  const removeRow = (id) => setRows((prev) => (prev.length === 1 ? prev : prev.filter((r) => r.id !== id)));

  const toggleSession = (rowId, s) => {
    const key = `${rowId}::${_sessionKey(s)}`;
    setDisabledIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleCreate = async () => {
    if (validPilotCount === 0) {
      toast.error(t('bulk_timer.error_no_pilots'));
      return;
    }
    if (totalActiveSessions === 0) {
      toast.error(t('bulk_timer.error_no_sessions'));
      return;
    }
    setCreating(true);
    let createdTotal = 0;
    let skippedTotal = 0;
    let failedPilots = [];
    try {
      for (const sp of rowSpecs) {
        if (!sp.pilot) continue;
        const activeSessions = sp.sessions.filter(
          (s) => !disabledIds.has(`${sp.row.id}::${_sessionKey(s)}`),
        );
        if (activeSessions.length === 0) continue;
        try {
          const res = await axios.post(`${API}/timers/bulk-create`, {
            pilot_name: sp.pilot.name,
            pilot_number: sp.pilot.number || null,
            sessions: activeSessions.map((s) => ({
              date: s.date,
              start_time: s.start_time,
              category: s.category,
              session_name: s.session_name,
              group: s.group,
            })),
          });
          createdTotal += res.data.created || 0;
          skippedTotal += res.data.skipped_past || 0;
          if (onCreated) onCreated(res.data);
        } catch {
          failedPilots.push(sp.pilot.name);
        }
      }
      if (failedPilots.length) {
        toast.error(t('bulk_timer.failed_toast', { pilots: failedPilots.join(', ') }));
      }
      if (createdTotal > 0) {
        const base = validPilotCount > 1
          ? t('bulk_timer.success_multi', { count: createdTotal, pilots: validPilotCount })
          : t('bulk_timer.success_toast', { count: createdTotal });
        const msg = skippedTotal ? `✓ ${base} · ${skippedTotal} skipped` : `✓ ${base}`;
        toast.success(msg);
        onOpenChange(false);
        reset();
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent
        className="border-red-900/30 max-w-3xl max-h-[85vh] overflow-y-auto"
        style={{ backgroundColor: 'var(--dp-surface-0)', color: 'var(--dp-text-primary)' }}
        data-testid="bulk-timer-import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--dp-text-primary)' }}>
            <Upload className="w-5 h-5" style={{ color: 'var(--dp-primary)' }} />
            {t('bulk_timer.title')}
          </DialogTitle>
          <DialogDescription className="pt-1" style={{ color: 'var(--dp-text-secondary)' }}>
            <span dangerouslySetInnerHTML={{ __html: t('bulk_timer.description') }} />
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: file upload */}
        {!parsed && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <MultiFilePickerCard
                icon={<FileText className="w-5 h-5 text-red-400" />}
                label={t('bulk_timer.cronograma_label')}
                helper={t('bulk_timer.cronograma_helper')}
                required
                files={cronogramaFiles}
                onFiles={setCronogramaFiles}
                accept=".pdf"
                inputRef={cronoRef}
                testId="bulk-crono-file"
                maxFiles={3}
              />
              <FilePickerCard
                icon={<FileSpreadsheet className="w-5 h-5 text-green-500" />}
                label={t('bulk_timer.grupos_label')}
                helper={t('bulk_timer.grupos_helper')}
                file={gruposFile}
                onFile={setGruposFile}
                accept=".xlsx,.xlsm"
                inputRef={gruposRef}
                testId="bulk-grupos-file"
              />
            </div>
            <div className="flex justify-end">
              <Button
                onClick={handleParse}
                disabled={parsing || !cronogramaFiles.length}
                className="h-12 min-h-[48px] bg-red-600 hover:bg-red-700 active:scale-95 shadow-md"
                data-testid="bulk-parse-btn"
              >
                {parsing ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />{t('bulk_timer.parsing')}</>
                ) : (
                  <>{t('bulk_timer.parse_btn')}</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: pilot + preview */}
        {parsed && (
          <div className="space-y-4" data-testid="bulk-parsed-preview">
            {/* Summary + reset */}
            <div className="flex items-center gap-2 flex-wrap text-xs" style={{ color: 'var(--dp-text-muted)' }}>
              <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--dp-success)' }} />
              <span>
                {t('bulk_timer.summary', {
                  sessions: parsed.sessions.length,
                  pilots: parsed.pilots.length,
                  categories: parsed.categories_detected.join(', '),
                })}
              </span>
              <button
                onClick={reset}
                className="ml-auto text-red-500 hover:text-red-400 active:scale-95"
                data-testid="bulk-reset-btn"
              >
                {t('bulk_timer.reload_files')}
              </button>
            </div>

            {/* Category picker (shared across all pilots in this batch) */}
            <div>
              <Label className="text-xs uppercase tracking-wide" style={{ color: 'var(--dp-text-secondary)' }}>
                {t('bulk_timer.category')}
              </Label>
              {parsed.categories_detected.length === 0 ? (
                <div
                  className="mt-1 rounded-md border border-amber-900/40 bg-amber-950/20 p-2 text-xs text-amber-300"
                  data-testid="bulk-no-categories-warn"
                >
                  <div className="inline-flex items-center gap-1 mb-1">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {t('bulk_timer.no_categories_title')}
                  </div>
                  <p className="text-amber-200/80 leading-relaxed">
                    {t('bulk_timer.no_categories_desc')}
                  </p>
                </div>
              ) : (
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger
                    className="h-12 min-h-[48px] mt-1"
                    style={{ backgroundColor: 'var(--dp-surface-1)', borderColor: 'var(--dp-border)', color: 'var(--dp-text-primary)' }}
                    data-testid="bulk-category-select"
                  >
                    <SelectValue placeholder={t('bulk_timer.pick_category')} />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: 'var(--dp-surface-1)', borderColor: 'var(--dp-border)', color: 'var(--dp-text-primary)' }}>
                    {parsed.categories_detected.map((c) => {
                      const col = getCategoryColor(c);
                      return (
                        <SelectItem key={c} value={c}>
                          <span className="inline-flex items-center gap-2">
                            <span className={`w-2.5 h-2.5 rounded-full ${col.dot}`} />
                            {c}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )}
              {selectedCategory && (
                <Badge
                  className={`mt-2 ${getCategoryColor(selectedCategory).bg} ${getCategoryColor(selectedCategory).text} border ${getCategoryColor(selectedCategory).border}`}
                >
                  {selectedCategory}
                </Badge>
              )}
            </div>

            {/* Pilots list (multi) */}
            <div className="space-y-2" data-testid="bulk-pilot-rows">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wide" style={{ color: 'var(--dp-text-secondary)' }}>
                  {t('bulk_timer.pilots_label', { valid: validPilotCount, total: rows.length })}
                </Label>
                <button
                  onClick={addRow}
                  className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-400 active:scale-95 h-11 min-h-[44px] px-2 rounded-md"
                  data-testid="bulk-add-pilot-btn"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {t('bulk_timer.add_pilot')}
                </button>
              </div>
              {rowSpecs.map((sp, idx) => (
                <PilotRow
                  key={sp.row.id}
                  index={idx}
                  spec={sp}
                  pilots={parsed.pilots}
                  onChange={(patch) => updateRow(sp.row.id, patch)}
                  onRemove={() => removeRow(sp.row.id)}
                  canRemove={rows.length > 1}
                />
              ))}
            </div>

            {/* Session preview — grouped by pilot */}
            <div
              className="rounded-lg overflow-hidden"
              style={{ backgroundColor: 'color-mix(in srgb, var(--dp-surface-1) 40%, transparent)', border: '1px solid var(--dp-border)' }}
              data-testid="bulk-session-preview"
            >
              <div className="px-3 py-2 text-xs flex items-center gap-2" style={{ backgroundColor: 'color-mix(in srgb, var(--dp-surface-1) 70%, transparent)', borderBottom: '1px solid var(--dp-border)', color: 'var(--dp-text-secondary)' }}>
                <CalendarIcon className="w-3.5 h-3.5" />
                <span>
                  {t('bulk_timer.sessions_to_create', {
                    count: totalActiveSessions,
                    pilots: validPilotCount,
                    plural: validPilotCount !== 1 ? 's' : '',
                  })}
                </span>
              </div>
              {validPilotCount === 0 ? (
                <div className="px-3 py-6 text-center text-sm" style={{ color: 'var(--dp-text-muted)' }}>
                  {t('bulk_timer.sessions_empty')}
                </div>
              ) : (
                <div style={{ borderColor: 'var(--dp-border)' }} className="divide-y divide-[color:var(--dp-border)]">
                  {rowSpecs.filter((sp) => sp.pilot).map((sp) => (
                    <PilotSessionsBlock
                      key={sp.row.id}
                      spec={sp}
                      disabledIds={disabledIds}
                      onToggle={(s) => toggleSession(sp.row.id, s)}
                    />
                  ))}
                </div>
              )}
            </div>

            {parsed.warnings?.length > 0 && (
              <div className="rounded-md border border-amber-900/40 bg-amber-950/20 p-2 text-xs text-amber-300">
                <div className="inline-flex items-center gap-1 mb-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {t('bulk_timer.warnings_title', { count: parsed.warnings.length })}
                </div>
                <ul className="ml-4 list-disc space-y-0.5">
                  {parsed.warnings.slice(0, 3).map((w, i) => (
                    <li key={i} className="truncate">{w}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="bulk-cancel-btn"
            className="h-12 min-h-[48px] active:scale-95"
            style={{ borderColor: 'var(--dp-border)', color: 'var(--dp-text-secondary)' }}
          >
            {t('bulk_timer.cancel')}
          </Button>
          {parsed && (
            <Button
              onClick={handleCreate}
              disabled={creating || totalActiveSessions === 0 || validPilotCount === 0}
              className="h-12 min-h-[48px] bg-red-600 hover:bg-red-700 active:scale-95 shadow-md"
              data-testid="bulk-create-btn"
            >
              {creating ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />{t('bulk_timer.creating')}</>
              ) : (
                <><Users className="w-4 h-4 mr-1.5" />{t('bulk_timer.create', { count: totalActiveSessions })}</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PilotRow = ({ index, spec, pilots, onChange, onRemove, canRemove }) => {
  const { t } = useTranslation();
  const { row, xlsxPilot } = spec;
  const canPickFromXlsx = pilots.length > 0;
  return (
    <div
      className="rounded-lg p-3 space-y-2"
      style={{ backgroundColor: 'color-mix(in srgb, var(--dp-surface-1) 40%, transparent)', border: '1px solid var(--dp-border)' }}
      data-testid={`bulk-pilot-row-${index}`}
    >
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: 'var(--dp-text-muted)' }}>
          {t('bulk_timer.pilot_number_title', { n: index + 1 })}
        </span>
        {canPickFromXlsx && (
          <div className="inline-flex items-center gap-1 rounded-md p-0.5 ml-1" style={{ backgroundColor: 'color-mix(in srgb, var(--dp-surface-0) 60%, transparent)', border: '1px solid var(--dp-border)' }}>
            {['select', 'manual'].map((m) => (
              <button
                key={m}
                onClick={() => onChange({ mode: m })}
                className={`px-2 py-1 rounded text-[10px] font-medium transition-colors min-h-[28px] active:scale-95 ${
                  row.mode === m ? 'text-white' : ''
                }`}
                style={{
                  backgroundColor: row.mode === m ? 'var(--dp-surface-2)' : 'transparent',
                  color: row.mode === m ? 'var(--dp-text-primary)' : 'var(--dp-text-secondary)',
                }}
                data-testid={`bulk-pilot-mode-${index}-${m}`}
              >
                {m === 'select' ? t('bulk_timer.pilot_mode_select') : t('bulk_timer.pilot_mode_manual')}
              </button>
            ))}
          </div>
        )}
        <div className="flex-1" />
        {canRemove && (
          <button
            onClick={onRemove}
            className="h-11 w-11 min-h-[44px] min-w-[44px] flex items-center justify-center rounded hover:bg-red-900/40 active:scale-95"
            style={{ color: 'var(--dp-text-muted)' }}
            title={t('bulk_timer.remove_pilot_title')}
            data-testid={`bulk-remove-pilot-${index}`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {row.mode === 'manual' || !canPickFromXlsx ? (
        <div className="flex items-center gap-1.5">
          <Input
            value={row.number}
            onChange={(e) => onChange({ number: e.target.value })}
            placeholder={t('bulk_timer.pilot_num_placeholder')}
            className="h-12 min-h-[48px] w-16 tabular-metric text-center"
            style={{ backgroundColor: 'var(--dp-surface-1)', borderColor: 'var(--dp-border)', color: 'var(--dp-text-primary)' }}
            data-testid={`bulk-pilot-manual-num-${index}`}
          />
          <Input
            value={row.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder={t('bulk_timer.pilot_name_placeholder')}
            className="h-12 min-h-[48px] flex-1"
            style={{ backgroundColor: 'var(--dp-surface-1)', borderColor: 'var(--dp-border)', color: 'var(--dp-text-primary)' }}
            data-testid={`bulk-pilot-manual-name-${index}`}
          />
        </div>
      ) : (
        <Select
          value={row.number}
          onValueChange={(v) => onChange({ number: v })}
        >
          <SelectTrigger
            className="h-12 min-h-[48px]"
            style={{ backgroundColor: 'var(--dp-surface-1)', borderColor: 'var(--dp-border)', color: 'var(--dp-text-primary)' }}
            data-testid={`bulk-pilot-select-${index}`}
          >
            <SelectValue placeholder={t('bulk_timer.pilot_select_placeholder')} />
          </SelectTrigger>
          <SelectContent className="max-h-64" style={{ backgroundColor: 'var(--dp-surface-1)', borderColor: 'var(--dp-border)', color: 'var(--dp-text-primary)' }}>
            {pilots.map((p) => (
              <SelectItem key={p.number} value={p.number}>
                <span className="tabular-metric">#{p.number}</span>{' '}{p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <div className="grid grid-cols-2 gap-2">
        <GroupPicker
          label={t('bulk_timer.group_entren')}
          value={row.entrenGroup}
          onChange={(v) => onChange({ entrenGroup: v })}
          options={['A', 'B', 'C']}
          testId={`bulk-entren-${index}`}
          autoLabel={xlsxPilot?.entrenamiento_group}
        />
        <GroupPicker
          label={t('bulk_timer.group_clasif')}
          value={row.clasifGroup}
          onChange={(v) => onChange({ clasifGroup: v })}
          options={['A', 'B', 'C']}
          testId={`bulk-clasif-${index}`}
          autoLabel={xlsxPilot?.clasificacion_group}
        />
      </div>
    </div>
  );
};

const PilotSessionsBlock = ({ spec, disabledIds, onToggle }) => {
  const { t } = useTranslation();
  const col = getCategoryColor(spec.sessions[0]?.category || '');
  const activeCount = spec.sessions.filter(
    (s) => !disabledIds.has(`${spec.row.id}::${_sessionKey(s)}`),
  ).length;
  return (
    <div className="p-2">
      <div className="flex items-center gap-2 px-1 py-1 text-xs">
        <span className={`w-2 h-2 rounded-full ${col.dot}`} />
        <span className="font-medium" style={{ color: 'var(--dp-text-primary)' }}>{spec.pilot.name}</span>
        {spec.pilot.number && (
          <span className="tabular-metric" style={{ color: 'var(--dp-text-muted)' }}>#{spec.pilot.number}</span>
        )}
        <span style={{ color: 'var(--dp-text-muted)' }}>·</span>
        <span style={{ color: 'var(--dp-text-secondary)' }}>
          {t('bulk_timer.sessions_summary', { active: activeCount, total: spec.sessions.length })}
        </span>
      </div>
      {spec.sessions.length === 0 ? (
        <div className="px-2 py-2 text-xs italic" style={{ color: 'var(--dp-text-muted)' }}>
          {t('bulk_timer.sessions_none_for_pilot')}
        </div>
      ) : (
        <div className="pl-2">
          {spec.sessions.map((s) => {
            const key = `${spec.row.id}::${_sessionKey(s)}`;
            const enabled = !disabledIds.has(key);
            return (
              <label
                key={key}
                className={`flex items-center gap-3 px-2 py-2 min-h-[44px] cursor-pointer rounded transition-colors ${
                  enabled ? '' : 'opacity-40'
                }`}
                style={{}}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--dp-surface-1) 40%, transparent)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                data-testid={`bulk-session-${spec.row.id}-${_sessionKey(s)}`}
              >
                <Checkbox
                  checked={enabled}
                  onCheckedChange={() => onToggle(s)}
                  className="border-[color:var(--dp-border)]"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm" style={{ color: 'var(--dp-text-primary)' }}>
                    {s.session_name}
                    {s.group && (
                      <Badge
                        className="ml-2 text-[10px] px-1.5 py-0"
                        style={{ backgroundColor: 'var(--dp-surface-2)', color: 'var(--dp-text-primary)' }}
                      >
                        {t('bulk_timer.group_badge', { g: s.group })}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: 'var(--dp-text-muted)' }}>
                    {s.date} · {s.start_time} · {s.category}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

const FilePickerCard = ({ icon, label, helper, required, file, onFile, accept, inputRef, testId }) => (
  <label
    className="flex items-center gap-3 rounded-lg p-3 cursor-pointer transition-colors hover:border-red-700"
    style={{
      backgroundColor: 'color-mix(in srgb, var(--dp-surface-1) 40%, transparent)',
      border: '1px solid var(--dp-border)',
      minHeight: '64px',
    }}
  >
    <div
      className="w-11 h-11 min-w-[44px] rounded-lg flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: 'var(--dp-surface-0)' }}
    >
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm flex items-center gap-1" style={{ color: 'var(--dp-text-primary)' }}>
        {label}{required && <span className="text-red-500">*</span>}
      </div>
      <div className="text-xs truncate" style={{ color: 'var(--dp-text-muted)' }}>
        {file ? file.name : helper}
      </div>
    </div>
    <input
      ref={inputRef}
      type="file"
      accept={accept}
      className="hidden"
      onChange={(e) => onFile(e.target.files?.[0] || null)}
      data-testid={testId}
    />
  </label>
);

// Variant of FilePickerCard that accepts multiple files (up to `maxFiles`).
// Used for Rotax karting weekends where the schedule ships as TWO PDFs
// (Viernes practice + Sábado/Domingo official event). The Card displays
// the count + first-file name and lets the user click again to add more.
const MultiFilePickerCard = ({ icon, label, helper, required, files, onFiles, accept, inputRef, testId, maxFiles = 3 }) => {
  const handleChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const merged = [...files];
    for (const f of picked) {
      if (merged.length >= maxFiles) break;
      // Dedup by name+size so re-selecting the same file doesn't duplicate.
      if (!merged.some((m) => m.name === f.name && m.size === f.size)) {
        merged.push(f);
      }
    }
    onFiles(merged);
    // Reset the input so the same file can be re-selected after removing.
    if (inputRef?.current) inputRef.current.value = '';
  };
  const removeAt = (idx, ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    onFiles(files.filter((_, i) => i !== idx));
  };
  return (
    <label
      className="flex items-center gap-3 rounded-lg p-3 cursor-pointer transition-colors hover:border-red-700"
      style={{
        backgroundColor: 'color-mix(in srgb, var(--dp-surface-1) 40%, transparent)',
        border: '1px solid var(--dp-border)',
        minHeight: '64px',
      }}
      data-testid={`${testId}-card`}
    >
      <div
        className="w-11 h-11 min-w-[44px] rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: 'var(--dp-surface-0)' }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm flex items-center gap-1" style={{ color: 'var(--dp-text-primary)' }}>
          {label}{required && <span className="text-red-500">*</span>}
          {files.length > 0 && (
            <span
              className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full"
              style={{
                backgroundColor: 'color-mix(in srgb, var(--dp-primary, #dc2626) 22%, transparent)',
                color: 'var(--dp-primary, #f87171)',
              }}
              data-testid={`${testId}-count`}
            >
              {files.length}
            </span>
          )}
        </div>
        {files.length === 0 ? (
          <div className="text-xs truncate" style={{ color: 'var(--dp-text-muted)' }}>
            {helper}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1 mt-1">
            {files.map((f, i) => (
              <span
                key={`${f.name}-${f.size}-${i}`}
                className="text-[11px] px-1.5 py-0.5 rounded truncate max-w-[220px] inline-flex items-center gap-1"
                style={{
                  backgroundColor: 'color-mix(in srgb, var(--dp-surface-2) 50%, transparent)',
                  color: 'var(--dp-text-secondary)',
                }}
                title={f.name}
                data-testid={`${testId}-item-${i}`}
              >
                <span className="truncate">{f.name}</span>
                <button
                  type="button"
                  onClick={(e) => removeAt(i, e)}
                  className="opacity-70 hover:opacity-100"
                  style={{ color: 'var(--dp-primary, #f87171)' }}
                  data-testid={`${testId}-remove-${i}`}
                  aria-label="Quitar archivo"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        className="hidden"
        onChange={handleChange}
        data-testid={testId}
      />
    </label>
  );
};


const GroupPicker = ({ label, value, onChange, options, testId, autoLabel }) => {
  const { t } = useTranslation();
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide inline-flex items-center gap-1" style={{ color: 'var(--dp-text-secondary)' }}>
        {label}
        {autoLabel && value === autoLabel && (
          <span className="text-[10px]" style={{ color: 'var(--dp-success)' }}>{t('bulk_timer.auto')}</span>
        )}
      </Label>
      <div className="flex items-center gap-1 mt-1 rounded-md p-1" style={{ backgroundColor: 'color-mix(in srgb, var(--dp-surface-1) 50%, transparent)', border: '1px solid var(--dp-border)' }}>
        <button
          onClick={() => onChange('')}
          className="px-2 min-h-[40px] min-w-[40px] text-xs rounded transition-colors active:scale-95"
          style={{
            backgroundColor: !value ? 'var(--dp-surface-2)' : 'transparent',
            color: !value ? 'var(--dp-text-primary)' : 'var(--dp-text-secondary)',
          }}
          data-testid={`${testId}-any`}
        >
          —
        </button>
        {options.map((g) => (
          <button
            key={g}
            onClick={() => onChange(g)}
            className="px-2 min-h-[40px] min-w-[40px] text-xs rounded tabular-metric transition-colors active:scale-95"
            style={{
              backgroundColor: value === g ? 'var(--dp-primary)' : 'transparent',
              color: value === g ? '#ffffff' : 'var(--dp-text-secondary)',
            }}
            data-testid={`${testId}-${g}`}
          >
            {g}
          </button>
        ))}
      </div>
    </div>
  );
};

function _sessionKey(s) {
  return `${s.date}_${s.start_time}_${s.category}_${s.session_name}_${s.group || 'X'}`;
}

export default BulkTimerImport;
